// Local persistence (D-28): IndexedDB for the workspace (F-18), file handles and settings; File System Access for .acproj.
// Nothing here ever leaves the user's machine.

const DB_NAME = 'aicompare';
const STORE = 'kv';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  dbPromise ??= openDb();
  return dbPromise;
}

export async function kvGet<T>(key: string): Promise<T | undefined> {
  try {
    const d = await db();
    return await new Promise<T | undefined>((resolve, reject) => {
      const req = d.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result as T | undefined);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
}

export async function kvSet(key: string, value: unknown): Promise<boolean> {
  try {
    const d = await db();
    await new Promise<void>((resolve, reject) => {
      const tx = d.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    return true;
  } catch {
    return false;
  }
}

export async function kvDelete(key: string): Promise<void> {
  try {
    const d = await db();
    await new Promise<void>((resolve) => {
      const tx = d.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    /* ignore */
  }
}

export interface AutosaveRecord {
  projectJson: string;
  savedAt: string;
  handles: Record<string, FileSystemFileHandle | undefined>;
  projectHandle?: FileSystemFileHandle;
  clean: boolean;
}

type PermissionHandle = FileSystemFileHandle & {
  queryPermission?: (o: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (o: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
};

/** Asks for read permission on a stored handle (one click, D-28). Must be called from a user gesture. */
export async function fileFromHandle(h: FileSystemFileHandle): Promise<File | null> {
  const ph = h as PermissionHandle;
  try {
    let p = (await ph.queryPermission?.({ mode: 'read' })) ?? 'granted';
    if (p !== 'granted') p = (await ph.requestPermission?.({ mode: 'read' })) ?? 'denied';
    if (p !== 'granted') return null;
    return await h.getFile();
  } catch {
    return null;
  }
}

export async function ensureWritable(h: FileSystemFileHandle): Promise<boolean> {
  const ph = h as PermissionHandle;
  try {
    let p = (await ph.queryPermission?.({ mode: 'readwrite' })) ?? 'granted';
    if (p !== 'granted') p = (await ph.requestPermission?.({ mode: 'readwrite' })) ?? 'denied';
    return p === 'granted';
  } catch {
    return false;
  }
}

interface PickerWindow {
  showOpenFilePicker?: (o: unknown) => Promise<FileSystemFileHandle[]>;
  showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle>;
  showDirectoryPicker?: (o?: unknown) => Promise<FileSystemDirectoryHandle>;
}

export const fsAccess = {
  get supported(): boolean {
    return typeof (window as PickerWindow).showSaveFilePicker === 'function';
  },
  async openProject(): Promise<{ file: File; handle: FileSystemFileHandle } | null> {
    const w = window as PickerWindow;
    if (!w.showOpenFilePicker) return null;
    const [h] = await w.showOpenFilePicker({
      types: [{ description: 'AiComp project', accept: { 'application/json': ['.acproj'] } }],
      excludeAcceptAllOption: false,
    });
    return { file: await h.getFile(), handle: h };
  },
  async openVideos(): Promise<{ file: File; handle: FileSystemFileHandle }[] | null> {
    const w = window as PickerWindow;
    if (!w.showOpenFilePicker) return null;
    const hs = await w.showOpenFilePicker({
      multiple: true,
      types: [
        { description: 'Video', accept: { 'video/*': ['.mp4', '.mov', '.m4v', '.mkv', '.webm'] } },
        { description: 'Image sequence (PNG / EXR)', accept: { 'image/png': ['.png'], 'image/x-exr': ['.exr'] } },
      ],
    });
    return Promise.all(hs.map(async (h) => ({ file: await h.getFile(), handle: h })));
  },
  /** Save dialog; startIn opens it in that folder (the project folder) when given. */
  async saveAs(suggestedName: string, description: string, ext: string, mime: string, startIn?: FileSystemDirectoryHandle | null): Promise<FileSystemFileHandle | null> {
    const w = window as PickerWindow;
    if (!w.showSaveFilePicker) return null;
    return w.showSaveFilePicker({ suggestedName, types: [{ description, accept: { [mime]: [ext] } }], ...(startIn ? { startIn } : {}) });
  },
  async pickDirectory(mode: 'read' | 'readwrite' = 'readwrite'): Promise<FileSystemDirectoryHandle | null> {
    const w = window as PickerWindow;
    if (!w.showDirectoryPicker) return null;
    return w.showDirectoryPicker({ mode });
  },
};

export async function writeFile(h: FileSystemFileHandle, data: Blob | string): Promise<void> {
  const ws = await h.createWritable();
  await ws.write(data);
  await ws.close();
}
